import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { InlineFieldTextarea } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { Check, ChevronDown, Pencil, Wrench } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Markdown from 'react-markdown'
import { toast } from 'sonner'
import { useAIAgent, useUpdateAIAgent } from '@/api/hooks'
import { BackButton } from '@/components/back-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { formatEventTime } from '@/lib/utils'
import { usePageTitle } from '@/lib/page-title'

export function AIAgentDetailPage({ agentKey }: { agentKey: string }) {
  const { data: agent, isLoading } = useAIAgent(agentKey)
  usePageTitle(agent?.name)
  const updateAgent = useUpdateAIAgent(agentKey)

  const [editingPrompt, setEditingPrompt] = useState(false)
  const [promptDraft, setPromptDraft] = useState<string | null>(null)

  const currentPrompt = promptDraft ?? agent?.system_prompt ?? ''

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    )
  }

  if (!agent) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Agent not found</p>
      </div>
    )
  }

  const tools = agent.runtime_tools ?? []
  const modelOptions = agent.model_options ?? []
  const savingModel = updateAgent.isPending && updateAgent.variables?.model_name !== undefined

  function handleModelChange(model: string) {
    if (model === agent?.runtime_model) return
    updateAgent.mutate(
      { model_name: model },
      {
        onSuccess: () => toast.success(`Now running on ${model}`),
        onError: () => toast.error('Failed to change the model'),
      },
    )
  }

  function handleSavePrompt() {
    if (currentPrompt === agent?.system_prompt) {
      setEditingPrompt(false)
      setPromptDraft(null)
      return
    }
    updateAgent.mutate(
      { system_prompt: currentPrompt },
      {
        onSuccess: () => {
          toast.success('System prompt updated')
          setEditingPrompt(false)
          setPromptDraft(null)
        },
        onError: () => toast.error('Failed to update system prompt'),
      },
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Header */}
      <div>
        <BackButton fallback={{ to: '/ai' }} fallbackLabel="Back to AI Agents" />
        <div className="mt-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{agent.name}</h1>
            <Badge variant={agent.enabled ? 'default' : 'secondary'}>
              {agent.enabled ? 'Active' : 'Disabled'}
            </Badge>
          </div>
          {agent.description && (
            <p className="mt-1 text-sm text-muted-foreground">{agent.description}</p>
          )}
        </div>
      </div>

      {/* Configuration + System Prompt */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <dt className="text-muted-foreground">Model</dt>
                {modelOptions.length > 0 ? (
                  <dd>
                    <ReviewChoiceGroup
                      label="Model"
                      value={
                        savingModel
                          ? (updateAgent.variables?.model_name ?? agent.runtime_model)
                          : agent.runtime_model
                      }
                      options={modelOptions.map((model) => ({ value: model, label: model }))}
                      onChange={handleModelChange}
                      disabled={savingModel}
                    />
                  </dd>
                ) : (
                  <dd className="font-mono text-xs font-medium">{agent.runtime_model}</dd>
                )}
              </div>
              {agent.effort && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Effort</dt>
                  <dd>
                    <Badge variant="outline">{agent.effort}</Badge>
                  </dd>
                </div>
              )}
              {agent.max_tokens != null && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Max Tokens</dt>
                  <dd className="font-medium">{agent.max_tokens.toLocaleString()}</dd>
                </div>
              )}
              {agent.max_tool_rounds != null && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Max Tool Rounds</dt>
                  <dd className="font-medium">{agent.max_tool_rounds}</dd>
                </div>
              )}
              {agent.category && agent.category !== 'general' && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Category</dt>
                  <dd>
                    <Badge variant="outline" className="capitalize">
                      {agent.category}
                    </Badge>
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base">Instructions</CardTitle>
                <p className="mt-1 text-xs text-muted-foreground">
                  Updated {formatEventTime(agent.updated_at)}
                </p>
              </div>
              {!editingPrompt && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => setEditingPrompt(true)}
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editingPrompt ? (
              <div className="space-y-3">
                <Label htmlFor="system-prompt" className="sr-only">
                  System Prompt
                </Label>
                <InlineFieldTextarea
                  id="system-prompt"
                  value={currentPrompt}
                  onChange={(e) => setPromptDraft(e.target.value)}
                  className="min-h-[400px] font-mono text-xs"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPromptDraft(null)
                      setEditingPrompt(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={updateAgent.isPending}
                    loadingText="Saving..."
                    size="sm"
                    onClick={handleSavePrompt}
                  >
                    <Check className="h-3.5 w-3.5" />
                    Save
                  </LoadingButton>
                </div>
              </div>
            ) : (
              <div className="prose prose-sm dark:prose-invert max-h-[400px] max-w-none overflow-y-auto rounded-md bg-muted p-4">
                <Markdown>{agent.system_prompt}</Markdown>
              </div>
            )}
            {agent.builtin_policy && (
              <details className="group mt-3 rounded-md border">
                <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-sm font-medium hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50">
                  Built-in rules, added after these instructions
                  <ChevronDown
                    className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <p className="border-t px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                  {agent.builtin_policy}
                </p>
              </details>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tools */}
      {tools.length > 0 && (
        <>
          <Separator />
          <div>
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
              <Wrench className="h-5 w-5" />
              Tools ({tools.length})
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {tools.map((tool) => (
                <Card key={tool.name}>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-mono text-sm break-all">{tool.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{tool.summary}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
